import { Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React from 'react';
import Colors from '../../utils/color';

import { activeCartSelector } from '../../utils/cart';
import { useAppSelector } from '../../app/hooks';
import { usePOSCart } from '../../hooks/usePOSCart.hook';
import { selectFeatureFlagHitl3 } from '../../redux/features/config/config.selector';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    width: '100%',
    backgroundColor: Colors.white,
    borderRadius: theme.spacing(0.25),
    padding: theme.spacing(1),
    borderBottom: `1px solid ${Colors.gallery}`,
  },
  // HITL 3.0: the ConsoleCard frame owns the card chrome + header, so the
  // container drops its own padding, border and background and lets the card
  // body handle scrolling (no nested 150px scroll box).
  containerHitl3: {
    padding: 0,
    backgroundColor: 'transparent',
    borderRadius: 0,
    borderBottom: 'none',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.25),
    borderBottom: `1px solid ${Colors.gallery}`,
  },
  headerTitle: {
    fontSize: '12px',
    fontWeight: 'bold',
    color: Colors.daintree,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  headerIcon: {
    width: '12px',
    height: '12px',
    backgroundColor: Colors.cerulean,
    borderRadius: '50%',
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(0.25),
    backgroundColor: Colors.daintree,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(0.125, 0.25),
  },
  controlIcon: {
    width: '8px',
    height: '8px',
    color: Colors.white,
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0),
    borderBottom: `1px solid ${Colors.gallery}`,
    marginBottom: theme.spacing(0.25),
    alignItems: 'start',
  },
  tableHeaderCell: {
    fontSize: '10px',
    fontWeight: 'bold',
    color: Colors.daintree,
  },
  tableContent: {
    maxHeight: '150px',
    overflowY: 'scroll',
  },
  // Defined AFTER tableContent so these win (JSS injects in definition order):
  // under hitl_3_0 the ConsoleCard body owns the scroll, so the table itself
  // must not add a second scrollbar (overflow:scroll also reserves a track even
  // when it doesn't overflow — that was the ghost second scrollbar).
  tableContentHitl3: {
    maxHeight: 'none',
    overflowY: 'visible',
  },
  priceHeader: {
    textAlign: 'right',
  },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0),
    alignItems: 'start',
    '&:last-child': {
      borderBottom: 'none',
    },
  },

  itemRowContent: {
    '&:hover': {
      backgroundColor: '#BFD7ED',
      cursor: 'pointer',
    },
  },

  itemName: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: Colors.daintree,
    alignSelf: 'start',
  },
  posId: {
    fontSize: '9px',
    color: Colors.daintree,
    alignSelf: 'start',
  },
  price: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: Colors.daintree,
    textAlign: 'right',
    alignSelf: 'start',
  },
  childItems: {
    padding: theme.spacing(0.25, 0),
    marginLeft: theme.spacing(1),
  },
  childItem: {
    display: 'inline-block',
    backgroundColor: Colors.gallery,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(0.125, 0.25),
    margin: theme.spacing(0.0625, 0.125, 0.0625, 0),
    fontSize: '8px',
    color: Colors.silverChalice,
  },
  childItemWithPrice: {
    backgroundColor: Colors.alto,
  },
  childItemPrice: {
    fontWeight: 'bold',
    color: Colors.daintree,
  },
  // hitl_3_0: match the totals card — item names and prices are NOT bold and use
  // the 12px "Subtotal" size. Defined after itemName/price so these win.
  itemNameHitl3: {
    fontWeight: 'normal',
    fontSize: '12px',
  },
  priceHitl3: {
    fontWeight: 'normal',
    fontSize: '12px',
  },
}));

interface OrderItem {
  name: string;
  pos_id: string;
  quantity: number;
  price: string;
  options?: OrderItem[];
}

interface PosCartContainerProps {
  items?: OrderItem[];
}

export default React.memo(function PosCartContainer({
  items = [],
}: PosCartContainerProps) {
  const classes = useStyles();
  const cartActive = useAppSelector(activeCartSelector);
  // hitl_3_0 only: the POS Cart lives inside a ConsoleCard (receipt icon +
  // "POS Cart" label + chevron), so the container's own blue dot + inline title
  // and card chrome are dropped here. Legacy keeps the standalone look.
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const { onItemClick, getPrice, setTimestamp } = usePOSCart();

  const renderChildItems = (item: OrderItem): React.ReactNode => {
    if (!item.options || item.options.length === 0) {
      return null;
    }

    const renderOptionsInline = (options: OrderItem[]): React.ReactNode => {
      return options.map((option, index) => {
        const hasPrice = parseFloat(option.price) > 0;
        return (
          <React.Fragment key={`${option.pos_id}-${index}`}>
            <span
              className={`${classes.childItem} ${
                hasPrice ? classes.childItemWithPrice : ''
              }`}
            >
              {option.name}
              {option.pos_id && ` (${option.pos_id})`}
              {hasPrice && (
                <span className={classes.childItemPrice}>
                  {' '}
                  - {getPrice(option.price)}
                </span>
              )}
            </span>
            {/* Recursively render nested options inline */}
            {option.options && option.options.length > 0 && (
              <span className={classes.childItems}>
                {renderOptionsInline(option.options)}
              </span>
            )}
          </React.Fragment>
        );
      });
    };

    return (
      <div className={classes.childItems}>
        {renderOptionsInline(item.options)}
      </div>
    );
  };

  const renderOrderItem = (item: OrderItem, index: number) => {
    const displayName =
      item.quantity > 1 ? `${item.quantity}x ${item.name}` : item.name;

    return (
      <div
        key={`${item.pos_id}-${index}`}
        className={classes.itemRowContent}
        onMouseDown={setTimestamp}
        onMouseUp={(evt) => onItemClick(item)}
      >
        <div className={classes.itemRow}>
          <div
            className={
              isHitl3
                ? `${classes.itemName} ${classes.itemNameHitl3}`
                : classes.itemName
            }
          >
            {displayName}
          </div>
          <div className={classes.posId}>{item.pos_id}</div>
          <div
            className={
              isHitl3 ? `${classes.price} ${classes.priceHitl3}` : classes.price
            }
          >
            {getPrice(item.price)}
          </div>
        </div>
        {renderChildItems(item)}
      </div>
    );
  };

  // Don't render if no items
  if (!items.length || !cartActive) {
    return <></>;
  }

  return (
    <div
      id="posCartContainer"
      className={
        isHitl3
          ? `${classes.container} ${classes.containerHitl3}`
          : classes.container
      }
    >
      {/* Legacy header (blue dot + inline title). Under hitl_3_0 the ConsoleCard
          header owns this, so it's dropped. */}
      {!isHitl3 && (
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <div className={classes.headerIcon}></div>
            POS Cart
          </div>
        </div>
      )}

      {items.length > 0 && (
        <>
          <div className={classes.tableHeader}>
            <div className={classes.tableHeaderCell}>Items</div>
            <div className={classes.tableHeaderCell}>Pos Id</div>
            <div
              className={`${classes.tableHeaderCell} ${classes.priceHeader}`}
            >
              Price
            </div>
          </div>

          <div
            className={
              isHitl3
                ? `${classes.tableContent} ${classes.tableContentHitl3}`
                : classes.tableContent
            }
          >
            {items.map((item, index) => renderOrderItem(item, index))}
          </div>
        </>
      )}
    </div>
  );
});
